import React, { useEffect, useState, useMemo, useRef } from "react";
import { Row, Col, Card, Container } from "react-bootstrap";
import { fetchNetworkOverview, fetchNetworkThreatSources, fetchNocAvailableDates, fetchClients } from "../../api/overviewApi";
import { useDataCache } from "../../contexts/DataCacheContext";

import OverviewCard from "../../components/OverviewCard";
import EndpointDetailsChart from "../../components/charts/EndpointDetailsChart";

import { Doughnut, Line, Bar } from "react-chartjs-2";
import { motion, AnimatePresence } from "framer-motion";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {
  FaExclamationTriangle,
  FaShieldAlt,
  FaServer,
  FaClock,
  FaNetworkWired,
  FaSpinner,
  FaChartLine,
  FaChartBar,
  FaCalendarAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaCompress,
  FaSlidersH,
  FaCheck,
  FaCodiepie,
  FaList,
} from "react-icons/fa";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
);

Tooltip.positioners.sideOffset = function (items, eventPosition) {
  const pos = Tooltip.positioners.nearest.call(this, items, eventPosition);
  if (!pos) return false;

  const chart = this.chart;
  const midX = (chart.chartArea.left + chart.chartArea.right) / 2;
  const offsetX = 28;
  const placeRight = pos.x < midX;

  return {
    x: placeRight ? pos.x + offsetX : pos.x - offsetX,
    y: pos.y,
    xAlign: placeRight ? "left" : "right",
    yAlign: "center",
  };
};

const NETWORK_CHART_MIN_HEIGHT = "46dvh";

const TREND_LINE_DASH_PATTERNS = [
  [],
  [6, 4],
  [2, 3],
  [8, 4, 2, 4],
  [2, 4],
  [2, 3],
  [10, 5],
];

const getTrendLineDash = (_label, index = 0) =>
  TREND_LINE_DASH_PATTERNS[index % TREND_LINE_DASH_PATTERNS.length];

const outlinedControlButtonStyle = {
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#ffffff",
  color: "#374151",
  padding: "5px 10px",
  fontSize: 11,
  fontWeight: 500,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  boxShadow: "none",
  transition: "all 0.2s ease",
  whiteSpace: "nowrap",
  cursor: "pointer",
};

const TrendInlineLegend = ({ items }) => {
  if (!items || items.length === 0) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))",
        gap: "10px 14px",
        padding: "6px 0 10px",
        borderBottom: "1px solid rgba(148,163,184,0.22)",
        marginBottom: 4,
      }}
    >
      {items.map((item, index) => {
        const dash = getTrendLineDash(item.label, index);
        const isActive = item.active !== false;
        return (
          <div
            key={item.id}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 7,
              opacity: isActive ? 1 : 0.4,
              minWidth: 0,
            }}
          >
            <svg width="30" height="10" aria-hidden="true" style={{ flexShrink: 0, marginTop: 3 }}>
              <line
                x1="0"
                y1="5"
                x2="30"
                y2="5"
                stroke={item.color || "#667eea"}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeDasharray={dash.length ? dash.join(" ") : undefined}
              />
            </svg>
            <span
              style={{
                fontSize: 11,
                color: "#6b7280",
                fontWeight: 400,
                lineHeight: 1.35,
                wordBreak: "break-word",
              }}
            >
              {item.label}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const HeadingIcon = ({ icon: Icon, color, bg }) => (
  <div
    style={{
      width: 32,
      height: 32,
      borderRadius: 10,
      background: bg || "rgba(111,66,193,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    }}
  >
    <Icon size={16} color={color || "var(--color-primary)"} />
  </div>
);

const LegendDropdown = ({ items, open, onToggleOpen, onToggleItem, variant = "default", icon: Icon = FaSlidersH }) => {
  if (!items || items.length === 0) return null;

  const isOutlined = variant === "outlined";

  return (
    <div className="position-relative">
      <button
        type="button"
        onClick={onToggleOpen}
        style={
          isOutlined
            ? outlinedControlButtonStyle
            : {
                borderRadius: 999,
                border: "1px solid var(--color-border)",
                background: "var(--color-primary-light)",
                color: "var(--color-text)",
                padding: "4px 10px",
                fontSize: 11,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                boxShadow: "var(--shadow-sm)",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }
        }
        onMouseEnter={(e) => {
          if (isOutlined) {
            e.currentTarget.style.borderColor = "#9ca3af";
            e.currentTarget.style.background = "#f9fafb";
          } else {
            e.currentTarget.style.background = "rgba(59,130,246,0.25)";
          }
        }}
        onMouseLeave={(e) => {
          if (isOutlined) {
            e.currentTarget.style.borderColor = "#d1d5db";
            e.currentTarget.style.background = "#ffffff";
          } else {
            e.currentTarget.style.background = "var(--color-primary-light)";
          }
        }}
      >
        <Icon size={10} />
        Legend
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "110%",
            right: 0,
            minWidth: 200,
            background: "var(--color-bg-card)",
            borderRadius: 14,
            border: "1px solid var(--color-border)",
            boxShadow: "var(--shadow-md)",
            padding: "8px 0",
            zIndex: 20,
          }}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => onToggleItem(item.id)}
              style={{
                width: "100%",
                border: "none",
                background: "transparent",
                padding: "6px 12px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                cursor: "pointer",
                fontSize: 12,
                color: item.active ? "var(--color-text)" : "var(--color-text-light)",
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: item.color || "var(--color-primary)",
                  }}
                />
                <span>{item.label}</span>
              </div>
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: 6,
                  border: `1px solid ${item.active ? "var(--color-primary)" : "var(--color-border)"}`,
                  background: item.active ? "var(--color-primary-light)" : "transparent",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {item.active && <FaCheck size={10} color="var(--color-primary)" />}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const fadeUp = {
  initial: { opacity: 0, y: 25 },
  animate: { opacity: 1, y: 0 },
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.12 } },
};

const NetworkOverview = ({ clientId, selectedClient }) => {
  // Log that component has mounted
  useEffect(() => {
    console.log("NetworkOverview component mounted, clientId=", clientId);
  }, []);

  // Legend dropdown state
  const [endpointLegendOpen, setEndpointLegendOpen] = useState(false);
  const [trendLegendOpen, setTrendLegendOpen] = useState(false);
  const [bubbleLegendOpen, setBubbleLegendOpen] = useState(false);
  const [momNocFullscreen, setMomNocFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [clientName, setClientName] = useState("");

  // Month filter state for NOC Endpoints Distribution (monthly data)
  const [nocSelectedMonth, setNocSelectedMonth] = useState(null);
  const [nocShowPicker, setNocShowPicker] = useState(false);
  const nocPickerRef = useRef(null);
  const [nocAvailableDates, setNocAvailableDates] = useState([]);

  // helper used by month effects to build YYYY-MM-DD string without timezone issues
  const formatDateForApi = (year, month) => {
    const m = String(month).padStart(2, "0");
    return `${year}-${m}-01`;
  };

  // Month filter state for Endpoint Details (monthly data)
  const [detailsSelectedMonth, setDetailsSelectedMonth] = useState(null);
  const [detailsShowPicker, setDetailsShowPicker] = useState(false);
  const detailsPickerRef = useRef(null);
  const [detailsAvailableDates, setDetailsAvailableDates] = useState([]);

  // Month filter state for Monthly Endpoint Trends
  const [trendSelectedMonth, setTrendSelectedMonth] = useState(null);
  const [trendShowPicker, setTrendShowPicker] = useState(false);
  const trendPickerRef = useRef(null);


  // Handle click outside picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (nocPickerRef.current && !nocPickerRef.current.contains(event.target)) {
        setNocShowPicker(false);
      }
      if (detailsPickerRef.current && !detailsPickerRef.current.contains(event.target)) {
        setDetailsShowPicker(false);
      }
      if (trendPickerRef.current && !trendPickerRef.current.contains(event.target)) {
        setTrendShowPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Month Picker Component (for monthly data charts like NOC & Endpoint Details)
  const MonthPickerPopup = ({ selectedMonth, setSelectedMonth, setShowPicker, availableDates = [] }) => {
    const currentDate = new Date();
    const [currentYear, setCurrentYear] = useState(selectedMonth ? selectedMonth.year : currentDate.getFullYear());
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    const isCurrentMonth = (month) => currentDate.getFullYear() === currentYear && currentDate.getMonth() === month;
    const isSelected = (month) => selectedMonth && selectedMonth.year === currentYear && selectedMonth.month === month + 1;
    const isFuture = (month) => currentYear > currentDate.getFullYear() || (currentYear === currentDate.getFullYear() && month > currentDate.getMonth());
    
    const hasData = (month) => {
      if (!availableDates || availableDates.length === 0) return false;
      return availableDates.some(d => d.year === currentYear && d.month === month + 1);
    };

    const handleMonthSelect = (month) => {
      if (!isFuture(month)) {
        const newMonth = { year: currentYear, month: month + 1 };
        console.log("MonthPickerPopup selecting month", newMonth);
        setSelectedMonth(newMonth);
        setShowPicker(false);
      }
    };

    const goToPrevYear = () => setCurrentYear(currentYear - 1);
    const goToNextYear = () => { if (currentYear < currentDate.getFullYear()) setCurrentYear(currentYear + 1); };

    return (
      <motion.div initial={{ opacity: 0, y: -10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -10, scale: 0.95 }} transition={{ duration: 0.15 }}
        style={{ position: "absolute", top: "100%", right: 0, marginTop: 8, background: "var(--color-bg-card)", borderRadius: 12, boxShadow: "0 10px 40px rgba(0,0,0,0.15)", border: "1px solid var(--color-border)", zIndex: 1000, minWidth: 200, padding: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, padding: "0 4px" }}>
          <button onClick={goToPrevYear} style={{ background: "transparent", border: "none", cursor: "pointer", padding: 4, borderRadius: 6, color: "var(--color-text)", display: "flex", alignItems: "center", justifyContent: "center" }}><FaChevronLeft size={10} /></button>
          <span style={{ fontWeight: 600, color: "var(--color-text)", fontSize: 14 }}>{currentYear}</span>
          <button onClick={goToNextYear} disabled={currentYear >= currentDate.getFullYear()} style={{ background: "transparent", border: "none", cursor: currentYear >= currentDate.getFullYear() ? "default" : "pointer", padding: 4, borderRadius: 6, color: currentYear >= currentDate.getFullYear() ? "var(--color-text-light)" : "var(--color-text)", display: "flex", alignItems: "center", justifyContent: "center", opacity: currentYear >= currentDate.getFullYear() ? 0.4 : 1 }}><FaChevronRight size={10} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
          {monthNames.map((month, index) => {
            const monthHasData = hasData(index);
            return (
              <div key={month} onClick={() => handleMonthSelect(index)}
                style={{
                  padding: "8px 4px",
                  textAlign: "center",
                  fontSize: 11,
                  fontWeight: isSelected(index) || isCurrentMonth(index) ? 600 : 400,
                  borderRadius: 8,
                  cursor: isFuture(index) ? "default" : "pointer",
                  background: isSelected(index) ? "#8b5cf6" : isCurrentMonth(index) ? "rgba(139, 92, 246, 0.15)" : "transparent",
                  color: isSelected(index) ? "#ffffff" : isFuture(index) ? "var(--color-text-light)" : isCurrentMonth(index) ? "#8b5cf6" : "var(--color-text)",
                  opacity: isFuture(index) ? 0.4 : 1,
                  transition: "all 0.15s ease",
                  position: "relative",
                  border: monthHasData && !isSelected(index) ? "1px solid #22c55e" : "1px solid transparent",
                }}
                onMouseEnter={(e) => { if (!isFuture(index) && !isSelected(index)) e.currentTarget.style.background = "rgba(139, 92, 246, 0.1)"; }}
                onMouseLeave={(e) => { if (!isFuture(index) && !isSelected(index)) e.currentTarget.style.background = isCurrentMonth(index) ? "rgba(139, 92, 246, 0.15)" : "transparent"; }}>
                {month}
                {monthHasData && !isSelected(index) && (
                  <div style={{ position: "absolute", top: 3, right: 3, width: 5, height: 5, borderRadius: "50%", background: "#22c55e" }} />
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--color-border)" }}>
          <button onClick={() => { setSelectedMonth(null); setShowPicker(false); }} style={{ background: "transparent", border: "none", color: "#8b5cf6", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "4px 8px", borderRadius: 6 }}>Clear</button>
          <button onClick={() => { setSelectedMonth({ year: currentDate.getFullYear(), month: currentDate.getMonth() + 1 }); setShowPicker(false); }} style={{ background: "#8b5cf6", border: "none", color: "#ffffff", fontSize: 10, fontWeight: 600, cursor: "pointer", padding: "4px 10px", borderRadius: 6, marginLeft: 6 }}>This Month</button>
        </div>
      </motion.div>
    );
  };

  // Month Badge Component (for monthly data charts)
  const MonthBadge = ({ selectedMonth, setSelectedMonth, showPicker, setShowPicker, pickerRef, availableDates = [], variant = "default" }) => {
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const formatMonth = (month) => month ? `${monthNames[month.month - 1]} ${month.year}` : "";
    const isOutlined = variant === "outlined";
    
    return (
      <div className="position-relative" ref={pickerRef}>
        <div
          onClick={() => {
            console.log("MonthBadge clicked, current showPicker", showPicker);
            setShowPicker(!showPicker);
          }}
          style={
            isOutlined
              ? {
                  ...outlinedControlButtonStyle,
                  border: selectedMonth ? "1px solid rgba(139, 92, 246, 0.35)" : "1px solid #d1d5db",
                }
              : {
                  borderRadius: 8,
                  background: selectedMonth ? "#ffffff" : "var(--color-bg-card)",
                  border: selectedMonth ? "1px solid rgba(139, 92, 246, 0.3)" : "1px solid var(--color-border)",
                  padding: "4px 10px",
                  fontSize: 11,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                }
          }
          onMouseEnter={(e) => {
            if (isOutlined) {
              e.currentTarget.style.borderColor = "#9ca3af";
              e.currentTarget.style.background = "#f9fafb";
            } else {
              e.currentTarget.style.borderColor = "rgba(139, 92, 246, 0.5)";
            }
          }}
          onMouseLeave={(e) => {
            if (isOutlined) {
              e.currentTarget.style.borderColor = selectedMonth ? "rgba(139, 92, 246, 0.35)" : "#d1d5db";
              e.currentTarget.style.background = "#ffffff";
            } else {
              e.currentTarget.style.borderColor = selectedMonth ? "rgba(139, 92, 246, 0.3)" : "var(--color-border)";
            }
          }}
        >
          <FaCalendarAlt size={10} color={isOutlined ? "#6b7280" : "#8b5cf6"} />
          <span style={{ color: selectedMonth ? "#8b5cf6" : (isOutlined ? "#374151" : "var(--color-text)"), fontWeight: 500 }}>
            {selectedMonth ? formatMonth(selectedMonth) : "Select Month"}
          </span>
          {selectedMonth ? (
            <FaTimes size={10} color="#8b5cf6" onClick={(e) => { e.stopPropagation(); setSelectedMonth(null); setShowPicker(false); }} style={{ cursor: "pointer" }} />
          ) : (
            !isOutlined && <FaChevronRight size={10} style={{ color: "var(--color-text-light)", transform: showPicker ? "rotate(90deg)" : "rotate(0deg)", transition: "transform 0.2s ease" }} />
          )}
        </div>
        <AnimatePresence>{showPicker && <MonthPickerPopup selectedMonth={selectedMonth} setSelectedMonth={setSelectedMonth} setShowPicker={setShowPicker} availableDates={availableDates} />}</AnimatePresence>
      </div>
    );
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch client name - first check selectedClient prop
  useEffect(() => {
    if (!clientId) {
      setClientName("");
      return;
    }

    // First, check if selectedClient prop has the name
    if (selectedClient?.client_name) {
      setClientName(selectedClient.client_name);
      return;
    }
  }, [clientId, selectedClient]);

  // Visibility maps
  const [endpointVisible, setEndpointVisible] = useState({});
  const [trendVisible, setTrendVisible] = useState({});
  const [bubbleVisible, setBubbleVisible] = useState({});

  const { getCachedData, setCachedData } = useDataCache();
  
  // Fetch client name from cache or API if not in selectedClient
  useEffect(() => {
    if (!clientId || selectedClient?.client_name) {
      return; // Already handled in previous useEffect
    }

    // Check cache
    const cacheKey = `client_name_${clientId}`;
    const cachedName = getCachedData(cacheKey);
    if (cachedName) {
      setClientName(cachedName);
      return;
    }

    // Fetch if not in cache
    const fetchClientName = async () => {
      try {
        const clientsList = await fetchClients();
        const foundClient = clientsList.find(c => c.client_id === parseInt(clientId));
        if (foundClient?.client_name) {
          setClientName(foundClient.client_name);
          // Cache the client name
          setCachedData(cacheKey, foundClient.client_name);
        } else {
          setClientName("");
        }
      } catch (error) {
        console.error("Error fetching client name:", error);
        setClientName("");
      }
    };

    fetchClientName();
  }, [clientId, selectedClient, getCachedData, setCachedData]);

  const [data, setData] = useState(null);
  const [threatSources, setThreatSources] = useState([]);
  const [loading, setLoading] = useState(false);
  const dataLoadedRef = useRef(new Set());
  const currentClientRef = useRef(clientId);

  useEffect(() => {
    if (currentClientRef.current !== clientId) {
      dataLoadedRef.current.clear();
      currentClientRef.current = clientId;
      // clear any month filters from previous client
      setNocSelectedMonth(null);
      setDetailsSelectedMonth(null);
      setTrendSelectedMonth(null);
    }

    if (!clientId) {
      setData(null);
      setThreatSources([]);
      setLoading(false);
      return;
    }

    const cacheKey = `network_${clientId}`;
    const cachedData = getCachedData("network_overview", clientId);

    // If we have cached data (including null), use it immediately
    if (cachedData !== undefined) {
      setData(cachedData);
      setLoading(false);

      if (!dataLoadedRef.current.has(cacheKey)) {
        dataLoadedRef.current.add(cacheKey);

        const cachedThreatSources = getCachedData("network_threat_sources", clientId);
        if (cachedThreatSources) {
          setThreatSources(Array.isArray(cachedThreatSources) ? cachedThreatSources : []);
        } else {
          fetchNetworkThreatSources(clientId)
            .then((res) => {
              const result = Array.isArray(res) ? res : [];
              setCachedData("network_threat_sources", result, clientId);
              setThreatSources(result);
            })
            .catch((error) => {
              console.error("Error fetching threat sources:", error);
              setThreatSources([]);
            });
        }
      }
      return;
    }

    // No cached data, fetch it
    if (dataLoadedRef.current.has(cacheKey)) {
      return;
    }

    setLoading(true);
    dataLoadedRef.current.add(cacheKey);

    fetchNetworkOverview(clientId)
      .then((result) => {
        setCachedData("network_overview", result, clientId);
        setData(result);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching network overview:", error);
        setCachedData("network_overview", null, clientId);
        setData(null);
        setLoading(false);
        dataLoadedRef.current.delete(cacheKey);
      });

    const cachedThreatSources = getCachedData("network_threat_sources", clientId);
    if (cachedThreatSources) {
      setThreatSources(Array.isArray(cachedThreatSources) ? cachedThreatSources : []);
    } else {
      fetchNetworkThreatSources(clientId)
        .then((res) => {
          const result = Array.isArray(res) ? res : [];
          setCachedData("network_threat_sources", result, clientId);
          setThreatSources(result);
        })
        .catch((error) => {
          console.error("Error fetching threat sources:", error);
          setThreatSources([]);
        });
    }
  }, [clientId, getCachedData, setCachedData]);

  // Fetch NOC available dates for month picker indicators
  useEffect(() => {
    if (!clientId) return;
    
    fetchNocAvailableDates(clientId)
      .then((dates) => {
        const arr = dates || [];
        setNocAvailableDates(arr);
        // Use same dates for endpoint details since they use same data source
        setDetailsAvailableDates(arr);

        // if no month selected yet, pick current month if it's available, otherwise the latest available
        if (!nocSelectedMonth && arr.length > 0) {
          const now = new Date();
          const current = arr.find(d => d.year === now.getFullYear() && d.month === now.getMonth() + 1);
          const defaultMonth = current || arr[arr.length - 1];
          setNocSelectedMonth(defaultMonth);
          setDetailsSelectedMonth(defaultMonth);
        }
      })
      .catch((error) => {
        console.error("Error fetching NOC available dates:", error);
        setNocAvailableDates([]);
        setDetailsAvailableDates([]);
      });
  }, [clientId]);

  // log month state so we know selection changed
  useEffect(() => {
    console.log("NetworkOverview state nocSelectedMonth changed:", nocSelectedMonth);
  }, [nocSelectedMonth]);

  // refetch threat sources when user picks a month (or clears filter)
  useEffect(() => {
    if (!clientId) return;

    const dateString = nocSelectedMonth
      ? formatDateForApi(nocSelectedMonth.year, nocSelectedMonth.month)
      : null;

    console.log("NetworkOverview - NOC month changed, date=", dateString);

    fetchNetworkThreatSources(clientId, dateString)
      .then((res) => {
        console.log("NetworkOverview - threatSources API returned", res);
        setThreatSources(Array.isArray(res) ? res : []);
      })
      .catch((error) => {
        console.error("Error fetching NOC distribution for date", dateString, error);
        setThreatSources([]);
      });
  }, [nocSelectedMonth, clientId]);

  // track details filter state for debugging
  useEffect(() => {
    console.log("NetworkOverview state detailsSelectedMonth changed:", detailsSelectedMonth);
  }, [detailsSelectedMonth]);

  // also observe when the picker popups open/close
  useEffect(() => {
    console.log("NetworkOverview nocShowPicker:", nocShowPicker);
  }, [nocShowPicker]);
  useEffect(() => {
    console.log("NetworkOverview detailsShowPicker:", detailsShowPicker);
  }, [detailsShowPicker]);
  useEffect(() => {
    console.log("NetworkOverview trendShowPicker:", trendShowPicker);
  }, [trendShowPicker]);

  // refetch endpoint data when details month changes (or clears)
  useEffect(() => {
    if (!clientId) return;

    const dateString = detailsSelectedMonth
      ? formatDateForApi(detailsSelectedMonth.year, detailsSelectedMonth.month)
      : null;

    console.log("NetworkOverview - details month changed, date=", dateString);

    fetchNetworkOverview(clientId, dateString)
      .then((result) => {
        console.log("NetworkOverview - networkOverview API returned", result);
        setData(result);
      })
      .catch((error) => {
        console.error("Error fetching endpoint details for date", dateString, error);
      });
  }, [detailsSelectedMonth, clientId]);

  // Always show the dashboard, even if data is null or has 0 values
  // Create default data structure if data is null
  const displayData = data || {
    summary: {
      totalNocEndpoints: 0,
      uniqueEndpointTypes: 0,
      topEndpoint: "",
      topEndpointCount: 0,
      lastUpdated: "",
    },
    endpointDistribution: { labels: [], values: [], colors: [] },
    endpointDetails: { labels: [], values: [], colors: [] },
    monthlyEndpointTrend: { labels: [], endpoints: {}, colors: {} },
    alertsTable: [],
  };

  // prepare values needed by chart hooks; keep hooks at top level so ESLint
  // doesn't complain about conditional invocation
  const endpointLabels = displayData.endpointDistribution?.labels || [];
  const endpointValues = displayData.endpointDistribution?.values || [];
  const maxCount = endpointValues.length > 0 ? Math.max(...endpointValues) : 1;

  // memoized details data - use API response directly when month selected, otherwise use trend extraction
  const detailsData = useMemo(() => {
    console.log("detailsData memo - detailsSelectedMonth:", detailsSelectedMonth, "endpointDetails:", displayData.endpointDetails);
    
    // When a month is selected, the API returns filtered data in endpointDetails
    if (detailsSelectedMonth && displayData.endpointDetails?.labels?.length > 0) {
      console.log("Using endpointDetails directly:", displayData.endpointDetails);
      return displayData.endpointDetails;
    }
    
    // Fallback: try to extract from trend data if available
    if (detailsSelectedMonth && displayData.monthlyEndpointTrend?.labels?.length) {
      const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const selLabel = `${monthNames[detailsSelectedMonth.month - 1]} ${detailsSelectedMonth.year}`;
      const idx = displayData.monthlyEndpointTrend.labels.indexOf(selLabel);
      console.log("Trying to extract from trend - selLabel:", selLabel, "idx:", idx);
      if (idx !== -1) {
        const endpoints = displayData.monthlyEndpointTrend.endpoints || {};
        const labels = Object.keys(endpoints);
        const values = labels.map((name) => endpoints[name][idx] || 0);
        const colors = labels.map((name) => displayData.monthlyEndpointTrend.colors[name] || "#667eea");
        console.log("Extracted from trend:", { labels, values, colors });
        return { labels, values, colors };
      }
    }
    
    console.log("Using default endpointDetails");
    return displayData.endpointDetails;
  }, [detailsSelectedMonth, displayData.monthlyEndpointTrend, displayData.endpointDetails]);

  // bar chart data for endpoint details — filter out zero-value endpoints so all-zero months show the empty state
const filteredDetails = (detailsData?.labels || []).reduce((acc, label, i) => {
    const value = (detailsData?.values || [])[i];
    if (value > 0) {
        acc.labels.push(label);
        acc.values.push(value);
        acc.colors.push((detailsData?.colors || [])[i] || "#667eea");
    }
    return acc;
}, { labels: [], values: [], colors: [] });

const barData = {
    labels: filteredDetails.labels,
    datasets: [
        {
            label: "Endpoint Count",
            data: filteredDetails.values,
            backgroundColor: filteredDetails.colors.map((c) => c + "99"),
            borderColor: filteredDetails.colors,
            borderWidth: 2,
            borderRadius: 8,
            barThickness: filteredDetails.labels.length === 1 ? 60 : undefined,
            maxBarThickness: 80,
        },
    ],
};

  // whether the details data contains any non-zero values (used for empty-state)
  const detailsHasData = Boolean(
    detailsData &&
      Array.isArray(detailsData.labels) &&
      detailsData.labels.length > 0 &&
      Array.isArray(detailsData.values) &&
      detailsData.values.some((v) => v > 0)
  );

  // memoized bubble source list respecting month filter
  const nocEndpointsForBubble = useMemo(() => {
    const buildFrom = (rows) => {
      const filteredRows = (rows || []).filter((src) => src.count > 0);
      const m = filteredRows.length > 0
        ? Math.max(...filteredRows.map((src) => src.count), 1)
        : 1;
      return filteredRows.map((src) => ({
        source: src.source,
        count: src.count,
        severity: m > 0 ? Math.max(1, Math.round(src.count / m * 10)) : 1,
      }));
    };

    if (nocSelectedMonth) {
      return buildFrom(threatSources);
    }
    if (threatSources && threatSources.length > 0) {
      return buildFrom(threatSources);
    }
    return endpointLabels.map((label, i) => ({
      source: label,
      count: endpointValues[i] || 0,
      severity: maxCount > 0 ? Math.max(1, Math.round((endpointValues[i] || 0) / maxCount * 10)) : 1,
    }));
  }, [threatSources, nocSelectedMonth, endpointLabels, endpointValues, maxCount]);

  const hasNoData = useMemo(() => {
    if (!displayData) return false;

    const hasSummary =
      displayData.summary && (displayData.summary.totalNocEndpoints > 0 || (displayData.summary.uniqueEndpointTypes > 0 && displayData.summary.uniqueEndpointTypes !== null && displayData.summary.uniqueEndpointTypes !== undefined));

    const hasDistribution = displayData.endpointDistribution && displayData.endpointDistribution.values && displayData.endpointDistribution.values.length > 0 && displayData.endpointDistribution.values.some((v) => v > 0);

    const hasAlerts = displayData.alertsTable && Array.isArray(displayData.alertsTable) && displayData.alertsTable.length > 0;

    const hasTrend =
      displayData.monthlyEndpointTrend &&
      displayData.monthlyEndpointTrend.endpoints &&
      Object.keys(displayData.monthlyEndpointTrend.endpoints).length > 0 &&
      Object.values(displayData.monthlyEndpointTrend.endpoints).some((arr) => Array.isArray(arr) && arr.length > 0 && arr.some((v) => v > 0));

    return !hasSummary && !hasDistribution && !hasAlerts && !hasTrend;
  }, [displayData]);

  // Show "No Data" message if there's no network data for the selected client
  if (clientId && !loading && hasNoData) {
    const clientName = selectedClient?.client_name || "selected client";
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(108,117,125,0.1) 0%, rgba(108,117,125,0.05) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid rgba(108,117,125,0.15)",
                  }}
                >
                  <FaNetworkWired size={48} color="var(--color-text-light)" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  No Network Data Available
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                    marginBottom: 32,
                  }}
                >
                  There is no network data available for selected client <strong>{clientName}</strong>.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  style={{
                    padding: "16px 24px",
                    borderRadius: 12,
                    background: "rgba(108,117,125,0.08)",
                    border: "1px solid rgba(108,117,125,0.15)",
                    fontSize: 14,
                    color: "var(--color-text-light)",
                    maxWidth: 400,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FaExclamationTriangle size={16} />
                    <span>Network data will appear here once the client has NOC endpoints or network data recorded.</span>
                  </div>
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  if (!clientId) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(114, 105, 239, 0.1) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid var(--color-primary-light)",
                  }}
                >
                  <FaNetworkWired size={48} color="var(--color-primary)" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  Select a Client
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                  }}
                >
                  Please select a client from the search bar above to view their network overview data.
                </motion.p>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, var(--color-primary-light) 0%, rgba(114, 105, 239, 0.1) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid var(--color-primary-light)",
                  }}
                >
                  <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
                    <FaSpinner size={48} color="var(--color-primary)" />
                  </motion.div>
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  Preparing Network Overview
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                    marginBottom: 32,
                  }}
                >
                  Preparing network overview data for selected client...
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  style={{
                    display: "flex",
                    gap: 8,
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--color-primary)",
                    }}
                  />
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--color-primary)",
                    }}
                  />
                  <motion.div
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.4 }}
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--color-primary)",
                    }}
                  />
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  if (!clientId) {
    const clientName = selectedClient?.client_name || "selected client";
    return (
      <Container fluid className="py-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Card
            style={{
              borderRadius: 20,
              background: "var(--color-bg-card)",
              border: "1px solid var(--color-border)",
              boxShadow: "var(--shadow-md)",
              overflow: "hidden",
            }}
          >
            <Card.Body className="p-5">
              <div className="d-flex flex-column align-items-center justify-content-center text-center" style={{ minHeight: "60vh" }}>
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  style={{
                    width: 120,
                    height: 120,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, rgba(108, 117, 125, 0.1) 0%, rgba(108, 117, 125, 0.05) 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 24,
                    border: "3px solid rgba(108, 117, 125, 0.15)",
                  }}
                >
                  <FaChartLine size={48} color="var(--color-text-light)" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="fw-bold mb-3"
                  style={{
                    fontSize: 28,
                    color: "var(--color-text)",
                    marginBottom: 16,
                  }}
                >
                  No Network Data Available
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  style={{
                    fontSize: 16,
                    color: "var(--color-text-light)",
                    maxWidth: 500,
                    lineHeight: 1.6,
                    marginBottom: 32,
                  }}
                >
                  There is no network data available for selected client <strong>{clientName}</strong>.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  style={{
                    padding: "16px 24px",
                    borderRadius: 12,
                    background: "rgba(108, 117, 125, 0.08)",
                    border: "1px solid rgba(108, 117, 125, 0.15)",
                    fontSize: 14,
                    color: "var(--color-text-light)",
                    maxWidth: 400,
                  }}
                >
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FaExclamationTriangle size={16} />
                    <span>Network data will appear here once the client has network endpoints or alerts recorded</span>
                  </div>
                </motion.div>
              </div>
            </Card.Body>
          </Card>
        </motion.div>
      </Container>
    );
  }

  /* ---------------------------------------
      CHART DATA
  -----------------------------------------*/


  const endpointLegendItems = (displayData.endpointDistribution?.labels || []).map((label, i) => ({
    id: label,
    label,
    color: (displayData.endpointDistribution?.colors || [])[i] || "#667eea",
    active: endpointVisible[label] !== false,
  }));

  const toggleEndpointLabel = (id) => {
    setEndpointVisible((prev) => ({
      ...prev,
      [id]: prev[id] === false ? true : false,
    }));
  };

  const activeEndpointIndexes = (displayData.endpointDistribution?.labels || []).map((label, i) => (endpointVisible[label] === false ? null : i)).filter((i) => i !== null);

  const endpointData = {
    labels: activeEndpointIndexes.map((i) => (displayData.endpointDistribution?.labels || [])[i]),
    datasets: [
      {
        data: activeEndpointIndexes.map((i) => (displayData.endpointDistribution?.values || [])[i]),
        backgroundColor: activeEndpointIndexes.map((i) => (displayData.endpointDistribution?.colors || [])[i] || "#667eea"),
        borderWidth: 0,
      },
    ],
  };

  const trendLegendItems = Object.keys(displayData.monthlyEndpointTrend?.endpoints || {}).map((key) => ({
    id: key,
    label: key,
    color: (displayData.monthlyEndpointTrend?.colors || {})[key] || "#667eea",
    active: trendVisible[key] !== false,
  }));

  const toggleTrendSeries = (id) => {
    setTrendVisible((prev) => ({
      ...prev,
      [id]: prev[id] === false ? true : false,
    }));
  };

  // Filter trend data based on selected month
  const getFilteredTrendData = () => {
    const labels = displayData.monthlyEndpointTrend?.labels || [];
    const endpoints = displayData.monthlyEndpointTrend?.endpoints || {};
    
    if (labels.length === 0) {
      return { labels, endpoints };
    }
    
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    
    // Parse first available month from labels
    const firstLabel = labels[0];
    const firstParts = firstLabel.split(' ');
    const firstMonth = monthNames.indexOf(firstParts[0]) + 1;
    const firstYear = parseInt(firstParts[1]);
    
    // Determine target month (selected month or n-1: data for current month isn't available yet)
    const _now = new Date();
    let targetYear = _now.getMonth() === 0 ? _now.getFullYear() - 1 : _now.getFullYear();
    let targetMonth = _now.getMonth() === 0 ? 12 : _now.getMonth(); // getMonth() is 0-indexed, so getMonth()==2 means Mar, prev=Feb

    if (trendSelectedMonth) {
      targetYear = trendSelectedMonth.year;
      targetMonth = trendSelectedMonth.month;
    }
    
    // Generate all months from first available to target month
    const allMonths = [];
    let year = firstYear;
    let month = firstMonth;
    
    while (year < targetYear || (year === targetYear && month <= targetMonth)) {
      allMonths.push(`${monthNames[month - 1]} ${year}`);
      month++;
      if (month > 12) {
        month = 1;
        year++;
      }
    }
    
    // Always limit to last 6 months
    const trimmedMonths = allMonths.slice(Math.max(0, allMonths.length - 6));

    // Pad endpoints with 0 for missing months
    const paddedEndpoints = {};
    Object.keys(endpoints).forEach(key => {
      paddedEndpoints[key] = trimmedMonths.map(monthLabel => {
        const idx = labels.indexOf(monthLabel);
        return idx !== -1 ? endpoints[key][idx] : 0;
      });
    });
    
    return { labels: trimmedMonths, endpoints: paddedEndpoints };
  };

  const filteredTrendData = getFilteredTrendData();
  
  // whether the trend data contains any non-zero values (used for empty-state)
  const trendHasData = Boolean(
    filteredTrendData &&
      Array.isArray(filteredTrendData.labels) &&
      filteredTrendData.labels.length > 0 &&
      Object.keys(filteredTrendData.endpoints).length > 0 &&
      Object.values(filteredTrendData.endpoints).some(
        (arr) => Array.isArray(arr) && arr.some((v) => v > 0)
      )
  );
  
  const trendEndpointKeys = Object.keys(filteredTrendData.endpoints);

  const monthlyTrendData = {
    labels: filteredTrendData.labels,
    datasets: trendEndpointKeys
      .filter((key) => trendVisible[key] !== false)
      .map((key) => {
        const seriesIndex = trendEndpointKeys.indexOf(key);
        const color = (displayData.monthlyEndpointTrend?.colors || {})[key] || "#667eea";
        return {
          label: key,
          data: filteredTrendData.endpoints[key] || [],
          fill: false,
          borderColor: color,
          backgroundColor: color,
          borderWidth: 2,
          borderDash: getTrendLineDash(key, seriesIndex),
          tension: 0.35,
          pointRadius: 4,
          pointHoverRadius: 7,
          pointHitRadius: 12,
          pointBackgroundColor: "#ffffff",
          pointBorderColor: color,
          pointBorderWidth: 2,
          pointHoverBackgroundColor: "#ffffff",
          pointHoverBorderColor: color,
          pointHoverBorderWidth: 2,
        };
      }),
  };

  const trendDateRange =
    filteredTrendData.labels.length >= 2
      ? `${filteredTrendData.labels[0]} — ${filteredTrendData.labels[filteredTrendData.labels.length - 1]}`
      : filteredTrendData.labels[0] || "";

  const trendMaxValue = monthlyTrendData.datasets
    .flatMap((dataset) => dataset.data || [])
    .reduce((max, value) => Math.max(max, Number(value) || 0), 0);
  const trendYAxisMax = Math.max(120, Math.ceil(trendMaxValue / 20) * 20 || 0);

  const trendChartOptions = {
    maintainAspectRatio: false,
    layout: {
      padding: { top: 6, right: 10, bottom: 2, left: 2 },
    },
    interaction: {
      mode: "index",
      intersect: true,
    },
    animations: {
      radius: {
        duration: 180,
        easing: "easeOutQuad",
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        position: "sideOffset",
        backgroundColor: "#1e293b",
        titleColor: "#94a3b8",
        bodyColor: "#f8fafc",
        borderColor: "rgba(255,255,255,0.08)",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        displayColors: true,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 6,
        caretSize: 6,
        caretPadding: 12,
        titleFont: { size: 12, weight: "500" },
        bodyFont: { size: 12 },
        callbacks: {
          label: (context) => ` ${context.dataset.label}: ${context.parsed.y}`,
        },
      },
    },
    scales: {
      x: {
        border: { display: false },
        grid: {
          color: "rgba(148,163,184,0.18)",
          borderDash: [3, 4],
          lineWidth: 0.5,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        max: trendYAxisMax,
        border: { display: false },
        grid: {
          color: "rgba(148,163,184,0.18)",
          borderDash: [3, 4],
          lineWidth: 0.5,
        },
        ticks: {
          color: "#94a3b8",
          font: { size: 11 },
          stepSize: 20,
        },
      },
    },
  };


  const bubbleLegendItems = nocEndpointsForBubble.map((src) => ({
    id: src.source,
    label: src.source,
    color: "rgba(114,105,239,0.45)",
    active: bubbleVisible[src.source] !== false,
  }));

  const toggleBubbleSeries = (id) => {
    setBubbleVisible((prev) => ({
      ...prev,
      [id]: prev[id] === false ? true : false,
    }));
  };

  const bubbleData = {
    datasets: nocEndpointsForBubble
      .filter((src) => bubbleVisible[src.source] !== false)
      .map((src, i) => ({
        label: src.source,
        data: [{ x: i * 15, y: src.count, r: src.severity * 2 }],
        backgroundColor: "rgba(114,105,239,0.35)",
        borderColor: "var(--color-primary)",
      })),
  };

  // MoM NOC — stacked bar of month-over-month deltas per endpoint (limited to last 6 months of deltas)
  const momNocLabels = [];
  const momNocStackedDatasets = [];
  const hasNocMomData = (() => {
    if (!displayData?.monthlyEndpointTrend?.labels || !displayData?.monthlyEndpointTrend?.endpoints) return false;
    const allTrendLabels = displayData.monthlyEndpointTrend.labels;
    const allTrendEndpoints = displayData.monthlyEndpointTrend.endpoints;
    if (allTrendLabels.length < 2) return false;

    // Limit to last 7 months of source data so MoM shows 6 delta bars
    const momStart = Math.max(0, allTrendLabels.length - 7);
    const trendLabels = allTrendLabels.slice(momStart);
    const trendEndpoints = {};
    Object.entries(allTrendEndpoints).forEach(([key, counts]) => {
      trendEndpoints[key] = counts.slice(momStart);
    });

    for (let i = 1; i < trendLabels.length; i++) {
      momNocLabels.push(trendLabels[i]);
    }
    Object.entries(trendEndpoints).forEach(([endpointKey, counts]) => {
      const deltas = [];
      for (let i = 1; i < trendLabels.length; i++) {
        deltas.push((counts[i] || 0) - (counts[i - 1] || 0));
      }
      const color = (displayData.monthlyEndpointTrend.colors?.[endpointKey]) || "#10b981";
      momNocStackedDatasets.push({
        label: endpointKey,
        data: deltas,
        backgroundColor: color + "BB",
        borderColor: color,
        borderWidth: 1,
        borderRadius: 4,
        stack: "mom",
      });
    });
    return momNocLabels.length > 0 && momNocStackedDatasets.some((ds) => ds.data.some((v) => v !== 0));
  })();

  const momNocData = {
    labels: momNocLabels,
    datasets: momNocStackedDatasets,
  };

  /* ---------------------------------------
      START JSX
  -----------------------------------------*/

  return (
    <motion.div className="p-3" initial="initial" animate="animate" variants={staggerContainer}>
      {/* DASHBOARD BANNER */}
      <motion.div 
        variants={fadeUp} 
        className="mb-3"
        style={{ 
          marginTop: isMobile ? "0.5rem" : "-1.5rem"
        }}
      >
        <Card
          style={{
            borderRadius: 12,
            background: (() => {
              const isDark = document.body.classList.contains('dark-mode');
              return isDark 
                ? 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.15) 100%)'
                : 'linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.08) 100%)';
            })(),
            border: "1px solid var(--color-border)",
            boxShadow: "var(--shadow-sm)",
            overflow: "hidden",
          }}
        >
          <Card.Body className="p-3">
            <div className="d-flex align-items-center justify-content-between gap-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    background: "rgba(16,185,129,0.15)",
                    padding: 10,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <FaNetworkWired size={20} color="#10b981" />
                </div>

                <div>
                  <h4 className="fw-bold mb-0" style={{ fontSize: 18, color: "var(--color-text)" }}>
                    Network Dashboard For{clientName && <span style={{ color: "#10b981", marginLeft: 8 }}>{clientName}</span>}
                  </h4>
                  <p className="mb-0" style={{ fontSize: 12, color: "var(--color-text-light)" }}>
                    Monitor your network endpoints
                  </p>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2" style={{ flexWrap: "wrap" }}>
                <div
                  style={{
                    background: "rgba(16,185,129,0.1)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaNetworkWired size={14} color="#10b981" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#10b981" }}>Endpoints</span>
                </div>

                <div
                  style={{
                    background: "rgba(16,185,129,0.1)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaChartLine size={14} color="#10b981" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#10b981" }}>Trends</span>
                </div>

                <div
                  style={{
                    background: "rgba(16,185,129,0.1)",
                    borderRadius: 8,
                    padding: "6px 12px",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <FaChartBar size={14} color="#10b981" />
                  <span style={{ fontSize: 13, fontWeight: 500, color: "#10b981" }}>Analytics</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </motion.div>

      {/* SUMMARY CARDS */}
      <Row className="mb-4">
        <Col md={6} className={isMobile ? "mb-3" : ""}>
          <motion.div variants={fadeUp}>
            <OverviewCard title="Total NOC Endpoints" value={displayData.summary.totalNocEndpoints} icon={FaServer} color="var(--color-primary)" />
          </motion.div>
        </Col>

        <Col md={6}>
          <motion.div variants={fadeUp}>
            <OverviewCard title="Unique Network Tools" value={(displayData.summary?.uniqueEndpointTypes || 0).toLocaleString()} icon={FaShieldAlt} color="var(--color-primary)" />
          </motion.div>
        </Col>
      </Row>

      {/* ---------------------------------------------------------------------- */}
      {/*                    MONTHLY TREND + ENDPOINT DISTRIBUTION                */}
      {/* ---------------------------------------------------------------------- */}
      <Row className="mb-4 align-items-stretch">
        <Col md={8} className={`${isMobile ? "mb-3" : ""} d-flex`}>
          <motion.div variants={fadeUp} className="w-100">
            <Card
              className="h-100"
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
              }}
            >
              <Card.Body className="d-flex flex-column h-100">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-start gap-2">
                    <div
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 10,
                        background: "linear-gradient(135deg, rgba(139,92,246,0.18) 0%, rgba(99,102,241,0.12) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <FaChartLine size={15} color="#8b5cf6" />
                    </div>
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)", fontSize: 14, lineHeight: 1.3 }}>
                        Monthly Endpoint Trends (NOC)
                      </h6>
                      {trendDateRange && (
                        <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 400 }}>
                          {trendDateRange}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
                    <MonthBadge
                      selectedMonth={trendSelectedMonth}
                      setSelectedMonth={setTrendSelectedMonth}
                      showPicker={trendShowPicker}
                      setShowPicker={setTrendShowPicker}
                      pickerRef={trendPickerRef}
                      availableDates={nocAvailableDates}
                      variant="outlined"
                    />
                    <LegendDropdown
                      items={trendLegendItems}
                      open={trendLegendOpen}
                      onToggleOpen={() => setTrendLegendOpen((p) => !p)}
                      onToggleItem={toggleTrendSeries}
                      variant="outlined"
                      icon={FaList}
                    />
                  </div>
                </div>

                <TrendInlineLegend items={trendLegendItems} />

                <div style={{ flex: "1 1 0", minHeight: NETWORK_CHART_MIN_HEIGHT, position: "relative" }}>
                  {trendSelectedMonth && !trendHasData ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", flexDirection: "column", gap: 8 }}>
                      <FaCalendarAlt size={36} color="var(--color-text-light)" />
                      <div style={{ color: "var(--color-text-light)", fontSize: 14, textAlign: "center", maxWidth: 320 }}>
                        No endpoint trend data available for the selected month.
                      </div>
                    </div>
                  ) : (
                    <Line data={monthlyTrendData} options={trendChartOptions} />
                  )}
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        <Col md={4} className="d-flex">
          <motion.div variants={fadeUp} className="w-100">
            <Card
              className="h-100"
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
              }}
            >
              <Card.Body className="d-flex flex-column h-100">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <HeadingIcon icon={FaCodiepie} color="#f59e0b" bg="rgba(245,158,11,0.15)" />
                    <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>
                      Endpoint Distribution
                    </h6>
                  </div>

                  <LegendDropdown items={endpointLegendItems} open={endpointLegendOpen} onToggleOpen={() => setEndpointLegendOpen((p) => !p)} onToggleItem={toggleEndpointLabel} />
                </div>

                <div style={{ flex: "1 1 0", minHeight: NETWORK_CHART_MIN_HEIGHT, position: "relative" }}>
                  <Doughnut
                    data={endpointData}
                    options={{
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          display: true,
                          position: "bottom",
                          labels: {
                            color: (() => {
                              const isDark = document.body.classList.contains('dark-mode');
                              return isDark ? '#a0a4b8' : '#6c737f';
                            })(),
                            usePointStyle: true,
                            pointStyle: "circle",
                            padding: 8,
                            font: {
                              size: 11,
                              weight: 400,
                            },
                            boxWidth: 8,
                            boxHeight: 8,
                          },
                        },
                        tooltip: {
                          backgroundColor: "#000000",
                          titleColor: "#ffffff",
                          bodyColor: "#ffffff",
                          borderColor: "transparent",
                          borderWidth: 0,
                        },
                      },
                    }}
                  />
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      </Row>

      {/* ---------------------------------------------------------------------- */}
      {/*                      MoM NOC CHANGE + ENDPOINT DETAILS                 */}
      {/* ---------------------------------------------------------------------- */}

      {/* MoM Fullscreen Modal */}
      {momNocFullscreen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
          onClick={() => setMomNocFullscreen(false)}
        >
          <div
            style={{
              background: "var(--color-bg-card)",
              borderRadius: 18,
              width: "95vw",
              height: "90vh",
              display: "flex",
              flexDirection: "column",
              padding: 24,
              boxShadow: "0 25px 60px rgba(0,0,0,0.5)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>NOC Endpoint Month-over-Month Change</h5>
                {momNocLabels.length >= 2 && (
                  <span style={{ fontSize: 12, color: "var(--color-text-secondary, #94a3b8)" }}>
                    {momNocLabels[0]} → {momNocLabels[momNocLabels.length - 1]}
                  </span>
                )}
              </div>
              <button
                onClick={() => setMomNocFullscreen(false)}
                style={{
                  background: "rgba(239,68,68,0.1)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 8,
                  padding: "6px 12px",
                  cursor: "pointer",
                  color: "#ef4444",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                }}
              >
                <FaCompress size={12} /> Close
              </button>
            </div>
            <div style={{ flex: 1, position: "relative" }}>
              <Bar
                data={momNocData}
                options={{
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: true,
                      position: "bottom",
                      labels: { color: "var(--color-text)", font: { size: 12 }, boxWidth: 14, padding: 14 },
                    },
                    tooltip: {
                      backgroundColor: "var(--color-bg-card)",
                      titleColor: "var(--color-text)",
                      bodyColor: "var(--color-text)",
                      borderColor: "var(--color-border)",
                      borderWidth: 1,
                      callbacks: {
                        title: (items) => items[0]?.label || "",
                        label: (ctx) => { const d = ctx.parsed.y; return ` ${ctx.dataset.label}: ${d >= 0 ? "+" : ""}${d}`; },
                        footer: (items) => { const net = items.reduce((s, c) => s + c.parsed.y, 0); return `Net: ${net >= 0 ? "+" : ""}${net}`; },
                      },
                    },
                  },
                  scales: {
                    x: { stacked: true, ticks: { color: "var(--color-text)", font: { size: 12 } }, grid: { color: "rgba(148,163,184,0.1)" } },
                    y: {
                      stacked: true,
                      ticks: { color: "var(--color-text)", font: { size: 12 }, callback: (v) => (v >= 0 ? `+${v}` : `${v}`) },
                      grid: { color: "rgba(148,163,184,0.1)" },
                      border: { dash: [4, 4] },
                    },
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      <Row className="mb-4">
        <Col lg={6} className={isMobile ? "mb-3" : ""}>
          <motion.div variants={fadeUp} style={{ height: "100%" }}>
            <Card
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                height: "100%",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <HeadingIcon icon={FaChartBar} color="#10b981" bg="rgba(16,185,129,0.15)" />
                    <div>
                      <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>
                        Endpoint Month-over-Month Change
                      </h6>
                      {momNocLabels.length >= 2 && (
                        <span style={{ fontSize: 11, color: "var(--color-text-secondary, #94a3b8)" }}>
                          {momNocLabels[0]} → {momNocLabels[momNocLabels.length - 1]}
                        </span>
                      )}
                    </div>
                  </div>
                  {hasNocMomData && (
                    <button
                      onClick={() => setMomNocFullscreen(true)}
                      title="Expand chart"
                      style={{
                        background: "rgba(16,185,129,0.1)",
                        border: "1px solid rgba(16,185,129,0.3)",
                        borderRadius: 8,
                        padding: "6px 10px",
                        cursor: "pointer",
                        color: "#10b981",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        fontSize: 12,
                      }}
                    >
                      <FaExpand size={12} />
                      <span>Expand</span>
                    </button>
                  )}
                </div>

                <div style={{ minHeight: "45dvh", position: "relative" }}>
                  {!hasNocMomData ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "45dvh" }}>
                      <span style={{ color: "var(--color-text-secondary, #94a3b8)", fontSize: 13 }}>
                        Not enough trend data to compute month-over-month change.
                      </span>
                    </div>
                  ) : (
                    <Bar
                      data={momNocData}
                      options={{
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: true,
                            position: "bottom",
                            labels: {
                              color: "var(--color-text)",
                              font: { size: 11 },
                              boxWidth: 12,
                              padding: 10,
                            },
                          },
                          tooltip: {
                            backgroundColor: "var(--color-bg-card)",
                            titleColor: "var(--color-text)",
                            bodyColor: "var(--color-text)",
                            borderColor: "var(--color-border)",
                            borderWidth: 1,
                            callbacks: {
                              title: (items) => items[0]?.label || "",
                              label: (ctx) => {
                                const delta = ctx.parsed.y;
                                const sign = delta >= 0 ? "+" : "";
                                return ` ${ctx.dataset.label}: ${sign}${delta}`;
                              },
                              footer: (items) => {
                                const net = items.reduce((sum, ctx) => sum + ctx.parsed.y, 0);
                                const sign = net >= 0 ? "+" : "";
                                return `Net: ${sign}${net}`;
                              },
                            },
                          },
                        },
                        scales: {
                          x: {
                            stacked: true,
                            ticks: { color: "var(--color-text)", font: { size: 11 } },
                            grid: { color: "rgba(148,163,184,0.1)" },
                          },
                          y: {
                            stacked: true,
                            ticks: {
                              color: "var(--color-text)",
                              font: { size: 11 },
                              callback: (v) => (v >= 0 ? `+${v}` : `${v}`),
                            },
                            grid: { color: "rgba(148,163,184,0.1)" },
                            border: { dash: [4, 4] },
                          },
                        },
                      }}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>

        <Col lg={6}>
          <motion.div variants={fadeUp} style={{ height: "100%" }}>
            <Card
              style={{
                borderRadius: 18,
                background: "var(--color-bg-card)",
                border: "1px solid var(--color-border)",
                height: "100%",
              }}
            >
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center gap-2">
                    <HeadingIcon icon={FaServer} color="#10b981" bg="rgba(16,185,129,0.15)" />
                    <h6 className="fw-bold mb-0" style={{ color: "var(--color-text)" }}>
                      Endpoint Details
                    </h6>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <MonthBadge selectedMonth={detailsSelectedMonth} setSelectedMonth={setDetailsSelectedMonth} showPicker={detailsShowPicker} setShowPicker={setDetailsShowPicker} pickerRef={detailsPickerRef} availableDates={detailsAvailableDates} />
                  </div>
                </div>

                <div style={{ minHeight: "45dvh", display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
                  {filteredDetails._loading ? (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        background: "var(--color-bg-card)",
                        padding: "10px 18px", borderRadius: 10,
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                        border: "1px solid var(--color-border)",
                      }}>
                        <FaSpinner className="spin" size={14} color="#10b981" />
                        <span style={{ fontSize: 12, fontWeight: 500, color: "var(--color-text)" }}>Loading...</span>
                      </div>
                    </div>
                  ) : filteredDetails.labels.length === 0 ? (
                    <div style={{
                      display: "flex", flexDirection: "column",
                      alignItems: "center", justifyContent: "center",
                      flex: 1, gap: 10,
                    }}>
                      <span style={{ fontSize: 22 }}>📭</span>
                      <span style={{ fontSize: 13, color: "var(--color-text-light, #94a3b8)", textAlign: "center" }}>
                        No endpoint data available for this month.
                      </span>
                    </div>
                  ) : (
                    <EndpointDetailsChart
                      labels={filteredDetails.labels}
                      values={filteredDetails.values}
                      colors={filteredDetails.colors}
                    />
                  )}
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      </Row>
    </motion.div>
  );
};

export default NetworkOverview;
